const API_BASE_URL = 'https://hailp-back.antoinelair.fr/api';
const API_KEY = process.env.REACT_APP_API_KEY || '';

async function apiRequest(endpoint, options = {}) {
  const headers = {
    'Content-Type': 'application/json',
    'Accept': 'application/json',
    'X-API-Key': API_KEY,
    ...options.headers,
  };

  const response = await fetch(`${API_BASE_URL}${endpoint}`, {
    ...options,
    headers,
  });

  const data = await response.json();

  if (!response.ok) {
    const error = new Error(data.message || 'Une erreur est survenue');
    error.status = response.status;
    error.errors = data.errors;
    error.data = data.data;
    throw error;
  }

  return data;
}

export async function register(userData) {
  return apiRequest('/members/register', {
    method: 'POST',
    body: JSON.stringify(userData),
  });
}

export async function login(email, password) {
  return apiRequest('/members/login', {
    method: 'POST',
    body: JSON.stringify({ email, password }),
  });
}

export async function getProfile(token) {
  return apiRequest('/members/me', {
    headers: {
      'Authorization': `Bearer ${token}`,
    },
  });
}

export async function logout(token) {
  return apiRequest('/members/logout', {
    method: 'POST',
    headers: {
      'Authorization': `Bearer ${token}`,
    },
  });
}

export async function getSubscriptions() {
  return apiRequest('/subscriptions');
}

export async function getLevels() {
  return apiRequest('/levels');
}

export async function getGames() {
  return apiRequest('/games');
}

export async function getConfidants(token) {
  return apiRequest('/reports/confidants', {
    headers: {
      'Authorization': `Bearer ${token}`,
    },
  });
}

export async function createReport(token, confidantId, message) {
  const body = { message };
  if (confidantId !== null) {
    body.confidant_id = confidantId;
  }
  return apiRequest('/reports', {
    method: 'POST',
    headers: {
      'Authorization': `Bearer ${token}`,
    },
    body: JSON.stringify(body),
  });
}

export async function getMyParent(token) {
  return apiRequest('/members/parent', {
    headers: {
      'Authorization': `Bearer ${token}`,
    },
  });
}

export async function getChildren(token) {
  return apiRequest('/children', {
    headers: {
      'Authorization': `Bearer ${token}`,
    },
  });
}

export async function createChild(token, childData) {
  return apiRequest('/children', {
    method: 'POST',
    headers: {
      'Authorization': `Bearer ${token}`,
    },
    body: JSON.stringify(childData),
  });
}

export async function getMemberSubscription(token) {
  return apiRequest('/members/subscription', {
    headers: {
      'Authorization': `Bearer ${token}`,
    },
  });
}

export async function subscribe(token, subscriptionId) {
  return apiRequest(`/subscriptions/${subscriptionId}/subscribe`, {
    method: 'POST',
    headers: {
      'Authorization': `Bearer ${token}`,
    },
  });
}

export async function cancelSubscription(token) {
  return apiRequest('/members/subscription/cancel', {
    method: 'POST',
    headers: {
      'Authorization': `Bearer ${token}`,
    },
  });
}

// Badges
export async function getBadges() {
  return apiRequest('/badges');
}

export async function getMemberBadges(token) {
  return apiRequest('/members/badges', {
    headers: {
      'Authorization': `Bearer ${token}`,
    },
  });
}

// Mirror Emotions
export async function createMirrorSession(token, difficultyLevel) {
  return apiRequest('/mirror-emotions/sessions', {
    method: 'POST',
    headers: {
      'Authorization': `Bearer ${token}`,
    },
    body: JSON.stringify({ difficulty_level: difficultyLevel }),
  });
}

export async function getMirrorSession(token, sessionId) {
  return apiRequest(`/mirror-emotions/sessions/${sessionId}`, {
    headers: {
      'Authorization': `Bearer ${token}`,
    },
  });
}

export async function checkMemoryPair(token, sessionId, emotionId, guessedEmotionId) {
  return apiRequest(`/mirror-emotions/sessions/${sessionId}/memory/check`, {
    method: 'POST',
    headers: {
      'Authorization': `Bearer ${token}`,
    },
    body: JSON.stringify({ emotion_id: emotionId, guessed_emotion_id: guessedEmotionId }),
  });
}

export async function completeMemory(token, sessionId) {
  return apiRequest(`/mirror-emotions/sessions/${sessionId}/memory/complete`, {
    method: 'POST',
    headers: {
      'Authorization': `Bearer ${token}`,
    },
  });
}

export async function getAssociationPieces(token, sessionId) {
  return apiRequest(`/mirror-emotions/sessions/${sessionId}/association`, {
    headers: {
      'Authorization': `Bearer ${token}`,
    },
  });
}

export async function checkAssociation(token, sessionId, emotionId, emotionActionId) {
  return apiRequest(`/mirror-emotions/sessions/${sessionId}/association/check`, {
    method: 'POST',
    headers: {
      'Authorization': `Bearer ${token}`,
    },
    body: JSON.stringify({ emotion_id: emotionId, emotion_action_id: emotionActionId }),
  });
}

export async function completeAssociation(token, sessionId) {
  return apiRequest(`/mirror-emotions/sessions/${sessionId}/association/complete`, {
    method: 'POST',
    headers: {
      'Authorization': `Bearer ${token}`,
    },
  });
}

// Teams
export async function getClassTeams(token, classroomId) {
  return apiRequest(`/classrooms/${classroomId}/teams`, {
    headers: {
      'Authorization': `Bearer ${token}`,
    },
  });
}

export async function getMyTeam(token, classroomId, teamId) {
  return apiRequest(`/classrooms/${classroomId}/teams/${teamId}`, {
    headers: {
      'Authorization': `Bearer ${token}`,
    },
  });
}

// Friendship Adventure
export async function getAdventures(token) {
  return apiRequest('/friendship-adventure/adventures', {
    headers: {
      'Authorization': `Bearer ${token}`,
    },
  });
}

export async function createAdventureSession(token, adventureId) {
  return apiRequest('/friendship-adventure/sessions', {
    method: 'POST',
    headers: {
      'Authorization': `Bearer ${token}`,
    },
    body: JSON.stringify({ adventure_id: adventureId }),
  });
}

export async function getAdventureScenario(token, sessionId) {
  return apiRequest(`/friendship-adventure/sessions/${sessionId}/scenario`, {
    headers: {
      'Authorization': `Bearer ${token}`,
    },
  });
}

export async function identifyEmotion(token, sessionId, emotionId) {
  return apiRequest(`/friendship-adventure/sessions/${sessionId}/identify-emotion`, {
    method: 'POST',
    headers: {
      'Authorization': `Bearer ${token}`,
    },
    body: JSON.stringify({ emotion_id: emotionId }),
  });
}

export async function chooseAction(token, sessionId, choiceId) {
  return apiRequest(`/friendship-adventure/sessions/${sessionId}/choose-action`, {
    method: 'POST',
    headers: {
      'Authorization': `Bearer ${token}`,
    },
    body: JSON.stringify({ choice_id: choiceId }),
  });
}

export async function completeAdventureSession(token, sessionId) {
  return apiRequest(`/friendship-adventure/sessions/${sessionId}/complete`, {
    method: 'POST',
    headers: {
      'Authorization': `Bearer ${token}`,
    },
  });
}
